import { useState } from 'react';
import type { ToolCall } from '@shared/protocol';
import { CaretIcon } from './icons';

/**
 * The one field that tells you what a tool call is actually doing. Showing
 * `{"file_path": "...", "offset": 0, ...}` in the collapsed header is noise;
 * showing `src/index.ts` is the whole point.
 */
function summarise(tool: ToolCall): string {
  const input = tool.input;
  if (typeof input === 'string') return input;
  if (!input || typeof input !== 'object') return '';

  const o = input as Record<string, unknown>;
  const preferred = [
    'command',
    'file_path',
    'path',
    'pattern',
    'url',
    'query',
    'description',
    'prompt',
    'notebook_path',
  ];

  for (const key of preferred) {
    const v = o[key];
    if (typeof v === 'string' && v.trim()) return v.replace(/\s+/g, ' ').trim();
  }

  const first = Object.entries(o).find(([, v]) => typeof v === 'string' && v);
  return first ? `${first[0]}: ${String(first[1]).replace(/\s+/g, ' ').trim()}` : '';
}

function prettyInput(input: unknown): string {
  if (typeof input === 'string') return input;
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}

const MAX_PREVIEW = 20_000;

export function ToolCard({ tool }: { tool: ToolCall }) {
  const [open, setOpen] = useState(false);

  const running = tool.result === undefined;
  const failed = tool.result?.isError === true;
  const summary = summarise(tool);

  const resultText = tool.result?.text ?? '';
  const clipped = resultText.length > MAX_PREVIEW;

  return (
    <div className={`tool${failed ? ' tool--error' : ''}`}>
      <button
        className="tool__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <CaretIcon className={`tool__caret${open ? ' tool__caret--open' : ''}`} />
        <span className="tool__name">{tool.name}</span>
        <span className="tool__arg">{summary}</span>
        {tool.actingAs && (
          <span className="tool__as" title={tool.actingAs.email}>
            {tool.actingAs.name} olarak
          </span>
        )}
        {running ? (
          <span className="spinner" aria-label="running" />
        ) : (
          <span className="tool__state">{failed ? 'failed' : 'done'}</span>
        )}
      </button>

      {open && (
        <div className="tool__body">
          <div className="tool__section">
            <h4>Input</h4>
            <pre className="tool__pre">{prettyInput(tool.input)}</pre>
          </div>

          {tool.result && (
            <div className="tool__section">
              <h4>{failed ? 'Error' : 'Output'}</h4>
              <pre className={`tool__pre${failed ? ' tool__pre--error' : ''}`}>
                {clipped ? `${resultText.slice(0, MAX_PREVIEW)}\n\n… ${resultText.length - MAX_PREVIEW} more characters` : resultText || '(empty)'}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
